import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { NavBar } from 'components/NavBar/NavBar'
import { Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { BarFill, BarTrack } from 'pages/HrDashboard/hrLayout.styled'
import { PlanCode } from '../Operations.styled'
import useQueryPagination from 'hooks/useQueryPagination'
import { clearOperationPlans, getOperationPlans } from 'features/operations/plansSlice'
import { RootState, AppDispatch } from 'store'
import { getLiveTasks } from 'features/operations/opsLiveTasksSlice'
import { useOpsBootstrap } from 'hooks/useOpsBootstrap'
import { getParams } from 'utils/helpers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { IGetAllOperationPlansSingleResponse } from 'types/api/operationsPlansApi.interface'
import { IOpsAlert, IOpsLiveTask, OpsAlertSeverity } from 'types/operationsControl.interface'
import { IOpsRequest } from 'types/opsRequests.interface'
import { IGmRollupCoverage, IGmRollupPace, IGmRollupRow, IGmRollupStatus } from 'types/gmRollup.interface'
import { PLAN_STATUS_TO_CORE, taskCoreState } from '../Shared/opsControl'
import { requestSlaState } from '../Shared/opsRequestsData'
import { deptName, opsDepartments } from '../Shared/opsDepartments'
import { CoreStatePill } from '../Shared/OpsSharedComponents'
import { formatPlanProgress, planProgressPercent } from 'utils/planProgress'
import {
    AlertDot,
    AlertList,
    AlertRow,
    AlertTime,
    ColumnsWrap,
    DeptMatrixSection,
    EmptyStateWrap,
    KpiStrip,
    PlanCard,
    PlanList,
    QuickLinkChip,
    QuickLinksStrip,
    TrendArrow,
} from './Overview.styled'

const ALERT_COLOR: { [k in OpsAlertSeverity]: string } = {
    critical: tokens.status.danger.fg,
    warning: tokens.status.warning.fg,
    info: tokens.status.info.fg,
}

const QUICK_LINKS = [
    { name: 'Create a new plan', link: '/operations/plans/add' },
    { name: 'Safety overview', link: '/operations/safety' },
    { name: 'Safety incidents', link: '/operations/safety/incidents' },
    { name: 'Permits', link: '/operations/safety/permits' },
    { name: 'Shift programs', link: '/operations/shift-programs' },
    { name: 'Tasks', link: '/operations/tasks' },
    { name: 'Running tasks', link: '/operations/running-tasks' },
    { name: 'Completed tasks', link: '/operations/completed-tasks' },
    { name: 'Queue health', link: '/operations/queue-health' },
]

const OPEN_REQUEST_STATUSES = ['open', 'acknowledged', 'in_progress']

const STAFFED_COVERAGE: { [k: string]: IGmRollupCoverage } = {
    mining: { label: '8 of 8', tone: 'neutral' },
    plant: { label: '2 of 2', tone: 'neutral' },
    maintenance: { label: '1 of 2', tone: 'warning' },
    camp: { label: '1 of 2', tone: 'warning' },
}

const planPacePct = (deptKey: string, plans: IGetAllOperationPlansSingleResponse[]): number | null => {
    const deptPlans = plans.filter((p) => p.departmentKey === deptKey && p.targetValue > 0)
    const activePlans = deptPlans.filter((p) => p.status === 2)
    const scopedPlans = (activePlans.length ? activePlans : deptPlans).filter((p) => p.currentValue != null)
    if (!scopedPlans.length) return null
    const percents = scopedPlans
        .map((p) => planProgressPercent(p.currentValue, p.targetValue))
        .filter((pct): pct is number => pct != null)
    if (!percents.length) return null
    return Math.round(percents.reduce((sum, pct) => sum + pct, 0) / percents.length)
}

const deptPace = (kpiTemplate: string, deptTasks: IOpsLiveTask[], plans: IGetAllOperationPlansSingleResponse[]): IGmRollupPace => {
    if (kpiTemplate === 'mining' || kpiTemplate === 'plant') {
        const pct = planPacePct(kpiTemplate, plans)
        if (pct === null) return { label: '—', note: null, trend: null }
        if (kpiTemplate === 'mining') return { label: `${pct}%`, note: 'on track', trend: 'up' }
        return { label: `${pct}%`, note: 'feed down', trend: 'down' }
    }
    if (kpiTemplate === 'custom' || !deptTasks.length) return { label: '—', note: null, trend: null }
    const done = deptTasks.filter((t) => taskCoreState(t.taskStatus) === 'COMPLETED').length
    return { label: `tasks ${done} of ${deptTasks.length}`, note: null, trend: null }
}

const deptCoverage = (kpiTemplate: string): IGmRollupCoverage => {
    return STAFFED_COVERAGE[kpiTemplate] || { label: '—', tone: 'neutral' }
}

const rollupStatus = (
    blockedTasks: number,
    overdueCritical: boolean,
    requestsOverdue: number,
    coverage: IGmRollupCoverage,
    pace: IGmRollupPace,
): IGmRollupStatus => {
    if (overdueCritical) return { tone: 'danger', label: 'Behind' }
    if (blockedTasks > 0 || coverage.tone === 'danger') return { tone: 'danger', label: 'Behind' }
    if (requestsOverdue > 0 || coverage.tone === 'warning' || pace.trend === 'down') {
        return { tone: 'warning', label: 'Attention' }
    }
    return { tone: 'success', label: 'On track' }
}

const buildRollupRows = (
    departments: ReturnType<typeof opsDepartments>,
    liveTasks: IOpsLiveTask[],
    requests: IOpsRequest[],
    plans: IGetAllOperationPlansSingleResponse[],
): IGmRollupRow[] =>
    departments.map((d) => {
        const deptTasks = liveTasks.filter((t) => t.department === d.key)
        const blockedTasks = deptTasks.filter((t) => taskCoreState(t.taskStatus) === 'BLOCKED').length
        const inbound = requests.filter((r) => r.toDept === d.key && OPEN_REQUEST_STATUSES.includes(r.status))
        const requestsOverdue = inbound.filter((r) => requestSlaState(r) === 'overdue').length
        const overdueCritical = inbound.some((r) => r.priority === 'critical' && requestSlaState(r) === 'overdue')
        const pace = deptPace(d.kpiTemplate, deptTasks, plans)
        const coverage = deptCoverage(d.kpiTemplate)
        return {
            id: d.key,
            key: d.key,
            name: d.name,
            pace,
            blockers: blockedTasks,
            requestsOpen: inbound.length,
            requestsOverdue,
            overdueCritical,
            coverage,
            status: rollupStatus(blockedTasks, overdueCritical, requestsOverdue, coverage, pace),
        }
    })

const COVERAGE_COLOR: { [k in IGmRollupCoverage['tone']]: string | undefined } = {
    neutral: undefined,
    warning: tokens.status.warning.fg,
    danger: tokens.status.danger.fg,
}

const deptUnitCost = (
    deptKey: string,
    plans: IGetAllOperationPlansSingleResponse[],
): { actual: number; planned: number | null } | null => {
    const tagged = plans.filter((p) => p.departmentKey === deptKey && p.costPerUnit != null)
    const active = tagged.filter((p) => p.status === 2)
    const scoped = active.length ? active : tagged
    if (!scoped.length) return null
    const withPlanned = scoped.filter((p) => p.plannedCostPerUnit != null)
    return {
        actual: scoped.reduce((sum, p) => sum + (p.costPerUnit ?? 0), 0) / scoped.length,
        planned: withPlanned.length
            ? withPlanned.reduce((sum, p) => sum + (p.plannedCostPerUnit ?? 0), 0) / withPlanned.length
            : null,
    }
}

const buildRollupColumns = (plans: IGetAllOperationPlansSingleResponse[]): IHonestTableColumn<IGmRollupRow>[] => [
    {
        id: 'department',
        header: 'Department',
        sticky: true,
        minWidth: '12rem',
        cell: (item) => (
            <Cell>
                <BodyText weight='600'>{item.name}</BodyText>
                <PlanCode>{item.key}</PlanCode>
            </Cell>
        ),
    },
    {
        id: 'pace',
        header: 'Pace',
        minWidth: '9rem',
        cell: (item) => (
            <Flex center gap='0.25rem'>
                {item.pace.trend && (
                    <TrendArrow $color={item.pace.trend === 'up' ? tokens.status.success.fg : tokens.status.warning.fg}>
                        {item.pace.trend === 'up' ? '▲' : '▼'}
                    </TrendArrow>
                )}
                <OtherText grey={item.pace.label === '—'}>{item.pace.label}</OtherText>
                {item.pace.note && <OtherText grey>· {item.pace.note}</OtherText>}
            </Flex>
        ),
    },
    {
        id: 'blockers',
        header: 'Blockers',
        cell: (item) =>
            item.blockers > 0 ? (
                <BodyText weight='600' color={tokens.status.danger.fg}>{item.blockers}</BodyText>
            ) : (
                <OtherText color={tokens.text.muted}>0</OtherText>
            ),
    },
    {
        id: 'requests',
        header: 'Requests',
        minWidth: '10rem',
        cell: (item) => (
            <OtherText
                grey={item.requestsOpen === 0 && item.requestsOverdue === 0}
                color={item.requestsOverdue > 0 ? tokens.status.danger.fg : undefined}
            >
                {item.requestsOpen} open · {item.requestsOverdue} overdue
            </OtherText>
        ),
    },
    {
        id: 'coverage',
        header: 'Coverage',
        cell: (item) => (
            <OtherText grey={item.coverage.label === '—'} color={COVERAGE_COLOR[item.coverage.tone]}>
                {item.coverage.label}
            </OtherText>
        ),
    },
    {
        id: 'cost',
        header: 'Cost',
        minWidth: '10rem',
        cell: (item) => {
            const cost = deptUnitCost(item.key, plans)
            if (!cost) return <OtherText grey>—</OtherText>
            const overPlan = cost.planned != null && cost.actual > cost.planned
            return (
                <Flex center gap='0.25rem'>
                    <OtherText color={overPlan ? tokens.status.danger.fg : undefined}>
                        {`$${cost.actual.toFixed(2)} / t`}
                    </OtherText>
                    {cost.planned != null && <OtherText grey>{`(plan $${cost.planned.toFixed(2)})`}</OtherText>}
                </Flex>
            )
        },
    },
    {
        id: 'status',
        header: 'Status',
        cell: (item) => (
            <StatusPill status={item.status.tone} dot={item.overdueCritical}>{item.status.label}</StatusPill>
        ),
    },
]

const alertDept = (a: IOpsAlert): string | null => {
    if (a.planId) return 'mining'
    const text = `${a.title} ${a.description}`.toLowerCase()
    if (text.includes('coverage') || text.includes('gate guard')) return 'security'
    if (text.includes('camp')) return 'campServices'
    return null
}

export const Overview = () => {
    const dispatch = useDispatch<AppDispatch>()
    const [params] = useState<any>(getParams({ location, limit: 30 }))
    const navigate = useNavigate()
    const { data } = useSelector((state: RootState) => state.operationPlans)
    const { requests } = useSelector((state: RootState) => state.opsRequests)
    const apiLiveTasks = useSelector((state: RootState) => state.opsLiveTasks.tasks)
    const profiles = useSelector((state: RootState) => state.deptOpsProfiles.profiles)

    useOpsBootstrap()
    useEffect(() => {
        dispatch(getLiveTasks({}))
    }, [dispatch])

    useQueryPagination({ fetchFunctions: [getOperationPlans], cleanUpFunctions: [clearOperationPlans], params })

    const plans = data ?? []
    const rollupColumns = useMemo(() => buildRollupColumns(plans), [plans])
    const liveTasks = apiLiveTasks
    const departments = opsDepartments(profiles)
    const rollupRows = buildRollupRows(departments, liveTasks, requests, plans)
    const alerts: IOpsAlert[] = []

    const runningCount = liveTasks.filter((t) => t.taskStatus === 1).length
    const blockedCount = liveTasks.filter((t) => taskCoreState(t.taskStatus) === 'BLOCKED').length
    const queuedCount = liveTasks.filter((t) => t.taskStatus === 0).length
    const activeWorkers = 0
    const activeMachines = 0

    const progressPct = (p: IGetAllOperationPlansSingleResponse) =>
        p.progressPercent != null
            ? Math.min(100, Math.max(0, Math.round(p.progressPercent)))
            : planProgressPercent(p.currentValue, p.targetValue)

    return (
        <>
            <NavBar />
            <PageWrapper>
                    <H1WithBack title='Operations' arrow />
                <KpiStrip>
                    <MetricCard label='RUNNING TASKS' value={runningCount} accentColor={tokens.status.success.fg} />
                    <MetricCard
                        label='BLOCKED'
                        value={blockedCount}
                        accentColor={tokens.status.danger.fg}
                        trailing={blockedCount > 0 ? <StatusPill status='danger'>Attention</StatusPill> : undefined}
                    />
                    <MetricCard label='IN QUEUE' value={queuedCount} accentColor={tokens.status.info.fg} />
                    <MetricCard label='ACTIVE WORKERS' value={activeWorkers} accentColor={tokens.accent.base} />
                    <MetricCard label='ACTIVE MACHINES' value={activeMachines} accentColor={tokens.accent.base} />
                </KpiStrip>
                <DeptMatrixSection>
                    <Container>
                        <ContainerHeader>
                            <Flex width='100%' between center>
                                <TitleText>Departments</TitleText>
                                <OtherText grey>Worst signal sets the status</OtherText>
                            </Flex>
                        </ContainerHeader>
                        <ContainerContent>
                            <HonestTable
                                columns={rollupColumns}
                                data={rollupRows}
                                keyField='id'
                                onClick={(item) => navigate(`/operations/d/${item.key}`)}
                                emptyMessage='No departments reporting yet.'
                            />
                        </ContainerContent>
                    </Container>
                </DeptMatrixSection>
                <ColumnsWrap>
                    <Container>
                        <ContainerHeader>
                            <Flex width='100%' between center>
                                <TitleText>Active plans</TitleText>
                                <OtherText
                                    onClick={() => navigate('/operations/plans')}
                                    style={{ color: tokens.accent.base, cursor: 'pointer' }}
                                >
                                    All plans
                                </OtherText>
                            </Flex>
                        </ContainerHeader>
                        <ContainerContent>
                            {plans.length ? (
                                <PlanList>
                                    {plans.map((p) => (
                                        <PlanCard key={p.id} onClick={() => navigate(`/operations/plans/${p.id}`)}>
                                            <Flex between center gap='0.5rem'>
                                                <Flex column>
                                                    <BodyText weight='600'>{p.name}</BodyText>
                                                    <PlanCode>{p.code}</PlanCode>
                                                </Flex>
                                                <CoreStatePill state={PLAN_STATUS_TO_CORE[p.status]} />
                                            </Flex>
                                            <OtherText grey>{p.site?.name || '—'}</OtherText>
                                            {(() => {
                                                const pct = progressPct(p)
                                                return pct == null ? (
                                                    <OtherText grey>Not tracked</OtherText>
                                                ) : (
                                                    <BarTrack>
                                                        <BarFill $pct={pct} />
                                                    </BarTrack>
                                                )
                                            })()}
                                            <Flex between center gap='0.5rem'>
                                                <StatusPill status={p.queueHealth === 1 ? 'success' : 'danger'}>
                                                    {p.queueHealth === 1 ? 'On track' : 'Behind'}
                                                </StatusPill>
                                                <OtherText grey>
                                                    {formatPlanProgress(p.currentValue, p.targetValue, p.targetUnit?.name)} · {p.owner ? `${p.owner.firstName} ${p.owner.lastName}` : '—'}
                                                </OtherText>
                                            </Flex>
                                        </PlanCard>
                                    ))}
                                </PlanList>
                            ) : (
                                <EmptyStateWrap>
                                    <BodyText grey>No active operations. Create your first plan to start managing work.</BodyText>
                                    <Button variant='primary' onClick={() => navigate('/operations/plans/add')}>Create plan</Button>
                                </EmptyStateWrap>
                            )}
                        </ContainerContent>
                    </Container>
                    <Container>
                        <ContainerHeader>
                            <TitleText>Operational alerts</TitleText>
                        </ContainerHeader>
                        <ContainerContent>
                            {alerts.length ? (
                                <AlertList>
                                    {alerts.map((a) => {
                                        const dept = alertDept(a)
                                        return (
                                            <AlertRow key={a.id} $color={ALERT_COLOR[a.severity]}>
                                                <AlertDot color={ALERT_COLOR[a.severity]} />
                                                <Flex column gap='0.25rem' width='100%'>
                                                    <Flex between top gap='0.5rem'>
                                                        <BodyText weight='600'>{a.title}</BodyText>
                                                        <AlertTime>{a.time}</AlertTime>
                                                    </Flex>
                                                    <OtherText grey>{a.description}</OtherText>
                                                    {(dept || (a.actionLabel && a.actionPath)) && (
                                                        <Flex center gap='0.5rem'>
                                                            {dept && <StatusPill status='neutral'>{deptName(dept)}</StatusPill>}
                                                            {a.actionLabel && a.actionPath && (
                                                                <Button variant='black' size='sm' onClick={() => navigate(a.actionPath)}>
                                                                    {a.actionLabel}
                                                                </Button>
                                                            )}
                                                        </Flex>
                                                    )}
                                                </Flex>
                                            </AlertRow>
                                        )
                                    })}
                                </AlertList>
                            ) : (
                                <OtherText grey>No alerts.</OtherText>
                            )}
                        </ContainerContent>
                    </Container>
                </ColumnsWrap>
                <QuickLinksStrip>
                    {QUICK_LINKS.map((link) => (
                        <QuickLinkChip key={link.name} to={link.link}>{link.name}</QuickLinkChip>
                    ))}
                </QuickLinksStrip>
            </PageWrapper>
        </>
    )
}
